import React, { useState, forwardRef } from 'react';
import { HiMail, HiPhone, HiUser, HiOfficeBuilding } from 'react-icons/hi';
import { HiPaperAirplane } from "react-icons/hi2";
import { IoEarthOutline } from 'react-icons/io5';
import countries from '../../../../utils/COUNTRIESLIST';
import { toast } from 'react-toastify';

const ContactForm = forwardRef((props, ref) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    country: '',
    message: '',
    marketing: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://kapiltech-backend-ex.onrender.com/api/infor/InforContactForm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      if (data.success) {
        toast.success('We will get back to you soon!');
        resetForm();
      } else {
        toast.error(data.message || 'Something went wrong. Please try again.');
      }

    } catch (error) {
      console.error('Error in form submission:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      company: '',
      country: '',
      message: '',
      marketing: false
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="w-full bg-white py-24 lg:py-32 overflow-hidden" ref={ref}>
      <style>{`
        .contact-accent-text { color: #FF4A3D; }
        .contact-accent-bg { background-color: #FF4A3D; }
        .contact-dark-text { color: #1A2634; }
        .contact-input:focus { 
          border-color: #FF4A3D;
          box-shadow: 0 0 0 1px #FF4A3D;
        }
        .contact-submit-btn {
          background-color: #FF4A3D;
          transition: all 0.3s ease;
        }
        .contact-submit-btn:hover {
          background-color: #e63e32;
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(255, 74, 61, 0.2);
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-16 lg:gap-24 items-start">
          
          {/* Left Column: Info & Heading */}
          <div className="lg:col-span-5">
            <div className="mb-12">
              <span className="contact-accent-text font-bold tracking-wider uppercase text-sm mb-6 block">
                Get in Touch
              </span>
              <h2 className="contact-dark-text text-4xl md:text-5xl lg:text-6xl font-bold mb-8 leading-tight">
                Let's Start a Conversation
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-10">
                Ready to transform your enterprise with Infor solutions? Our experts are here to help you navigate your digital journey. Reach out today for a personalized consultation.
              </p>
            </div>

            {/* Contact Detail Cards */}
            <div className="space-y-6">
              <ContactInfoCard 
                icon={<HiMail className="w-6 h-6" />}
                title="Email Us"
                content="sales@kapiltech.com"
                link="mailto:sales@kapiltech.com"
              />
              <ContactInfoCard 
                icon={<HiPhone className="w-6 h-6" />}
                title="Call Us"
                content="+62 21 1234 5678"
                link="tel:+622112345678"
              />
              <ContactInfoCard 
                icon={<IoEarthOutline className="w-6 h-6" />}
                title="Office Location"
                content="Jakarta, Indonesia"
              />
            </div>
          </div>

          {/* Right Column: Form Card */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-gray-50">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InputField
                    label="Full Name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    icon={<HiUser className="w-5 h-5" />}
                  />
                  <InputField
                    label="Phone Number"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    icon={<HiPhone className="w-5 h-5" />}
                  />
                  <InputField
                    label="Email Address"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    icon={<HiMail className="w-5 h-5" />}
                  />
                  <InputField
                    label="Company"
                    name="company"
                    type="text"
                    required
                    value={formData.company}
                    onChange={handleChange}
                    icon={<HiOfficeBuilding className="w-5 h-5" />}
                  />
                  <div className="md:col-span-2">
                    <SelectField
                      label="Country"
                      name="country"
                      required
                      value={formData.country}
                      onChange={handleChange}
                      options={countries}
                      icon={<IoEarthOutline className="w-5 h-5" />}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <TextAreaField
                      label="Your Message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <CheckboxField
                      name="marketing"
                      checked={formData.marketing}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full md:w-auto inline-flex items-center justify-center px-10 py-4 text-white font-bold rounded-xl contact-submit-btn ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                  >
                    <span className="mr-2 text-lg">{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                    <HiPaperAirplane className={`h-5 w-5 -rotate-45 mb-1 transition-transform ${isSubmitting ? 'translate-x-1' : ''}`} />
                  </button>
                </div>
              </form>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
});

ContactForm.displayName = 'ContactForm';

const ContactInfoCard = ({ icon, title, content, link }) => (
  <div className="flex items-center gap-5 p-4 rounded-2xl border border-gray-50 hover:border-gray-100 hover:bg-gray-50/50 transition-all duration-300">
    <div className="w-12 h-12 rounded-xl contact-accent-bg flex items-center justify-center text-white shadow-lg shadow-[#FF4A3D]/20">
      {icon}
    </div>
    <div>
      <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-0.5">{title}</h4>
      {link ? (
        <a href={link} className="contact-dark-text font-semibold text-lg hover:text-[#FF4A3D] transition-colors">
          {content}
        </a>
      ) : (
        <p className="contact-dark-text font-semibold text-lg">{content}</p>
      )}
    </div>
  </div>
);

const InputField = ({ label, name, type, required, value, onChange, icon }) => (
  <div className="relative">
    <label className="text-gray-700 text-sm font-bold mb-2 block">{label}</label>
    <div className="relative">
      {icon && <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">{icon}</span>}
      <input
        type={type}
        name={name}
        required={required}
        value={value}
        onChange={onChange}
        className="w-full pl-11 pr-4 py-4 bg-gray-50/50 border border-gray-100 rounded-xl outline-none transition-all duration-200 contact-input text-gray-900 placeholder-gray-400 font-medium"
        placeholder={`Enter your ${label.toLowerCase()}`}
      />
    </div>
  </div>
);

const SelectField = ({ label, name, required, value, onChange, options, icon }) => (
  <div className="relative">
    <label className="text-gray-700 text-sm font-bold mb-2 block">{label}</label>
    <div className="relative">
      {icon && <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 z-10">{icon}</span>}
      <select
        name={name}
        required={required}
        value={value}
        onChange={onChange}
        className="w-full pl-11 pr-10 py-4 bg-gray-50/50 border border-gray-100 rounded-xl outline-none transition-all duration-200 contact-input text-gray-900 appearance-none font-medium cursor-pointer"
      >
        <option value="" disabled>Select your country</option>
        {options.map(country => (
          <option key={country.code} value={country.code}>{country.name}</option>
        ))}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400">
        <svg className="fill-current h-4 w-4" viewBox="0 0 20 20">
          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
        </svg>
      </div>
    </div>
  </div>
);

const TextAreaField = ({ label, name, value, onChange }) => (
  <div className="relative">
    <label className="text-gray-700 text-sm font-bold mb-2 block">{label}</label>
    <textarea
      name={name}
      value={value}
      onChange={onChange}
      rows="4"
      className="w-full px-4 py-4 bg-gray-50/50 border border-gray-100 rounded-xl outline-none transition-all duration-200 contact-input text-gray-900 placeholder-gray-400 font-medium resize-none"
      placeholder="How can we help you?"
    />
  </div>
);

const CheckboxField = ({ name, checked, onChange }) => (
  <label className="flex items-start space-x-3 cursor-pointer group">
    <div className="flex-shrink-0 mt-1">
      <input
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
        className="rounded border-gray-300 w-5 h-5 transition-colors cursor-pointer accent-[#FF4A3D]"
      />
    </div>
    <span className="text-sm text-gray-500 group-hover:text-gray-700 transition-colors leading-relaxed">
      By submitting this form, you acknowledge that we may use your personal information for marketing purposes in accordance with our privacy policy.
    </span>
  </label>
);

export default ContactForm;